import config from '../../config';
import { getAuthHeaders, getUserInfoFromToken, handleResponse, handleNoContentResponse } from './common';

const SUPPORT_URL = () => `${config.api.url}/support/tickets`;
const SUPPORT_BASE_URL = () => `${config.api.url}/support`;

function normalizeComment(dto) {
    if (!dto || typeof dto !== 'object') return dto;
    return {
        referenceId: dto.referenceId ?? dto.ReferenceId,
        body: dto.body ?? dto.Body,
        authorRole: dto.authorRole ?? dto.AuthorRole,
        authorName: dto.authorName ?? dto.AuthorName ?? null,
        createdAt: dto.createdAt ?? dto.CreatedAt,
    };
}

function normalizeCommentList(raw) {
    const items = raw?.$values ?? raw;
    if (!Array.isArray(items)) return [];
    return items.map(normalizeComment);
}

function normalizeTicket(dto) {
    if (!dto || typeof dto !== 'object') return dto;
    return {
        ...dto,
        referenceId: dto.referenceId ?? dto.ReferenceId,
        type: dto.type ?? dto.Type,
        severity: dto.severity ?? dto.Severity,
        title: dto.title ?? dto.Title,
        description: dto.description ?? dto.Description,
        status: dto.status ?? dto.Status,
        isFlagged: dto.isFlagged ?? dto.IsFlagged ?? false,
        flaggedAt: dto.flaggedAt ?? dto.FlaggedAt ?? null,
        groupReferenceId: dto.groupReferenceId ?? dto.GroupReferenceId ?? null,
        createdByRole: dto.createdByRole ?? dto.CreatedByRole,
        createdByName: dto.createdByName ?? dto.CreatedByName ?? null,
        phiStatus: dto.phiStatus ?? dto.PhiStatus,
        excludeFromAiProcessing: dto.excludeFromAiProcessing ?? dto.ExcludeFromAiProcessing,
        containsAttachmentsWithPossiblePhi:
            dto.containsAttachmentsWithPossiblePhi ?? dto.ContainsAttachmentsWithPossiblePhi,
        hasAttachments: dto.hasAttachments ?? dto.HasAttachments,
        attachmentCount: dto.attachmentCount ?? dto.AttachmentCount,
        commentCount: dto.commentCount ?? dto.CommentCount ?? 0,
        comments: normalizeCommentList(dto.comments ?? dto.Comments),
        attachments: normalizeAttachmentList(dto.attachments ?? dto.Attachments),
        createdAt: dto.createdAt ?? dto.CreatedAt,
        lastModifiedAt: dto.lastModifiedAt ?? dto.LastModifiedAt ?? dto.updatedAt ?? dto.UpdatedAt ?? dto.createdAt ?? dto.CreatedAt,
        resolvedAt: dto.resolvedAt ?? dto.ResolvedAt,
        updatedAt: dto.updatedAt ?? dto.UpdatedAt,
    };
}

function normalizeTicketList(raw) {
    const items = raw?.$values ?? raw;
    if (!Array.isArray(items)) return [];
    return items.map(normalizeTicket);
}

function normalizeTriageFile(dto) {
    if (!dto || typeof dto !== 'object') return dto;
    return {
        repo: dto.repo ?? dto.Repo,
        relativePath: dto.relativePath ?? dto.RelativePath,
        isActive: dto.isActive ?? dto.IsActive,
        lastSeenAtUtc: dto.lastSeenAtUtc ?? dto.LastSeenAtUtc,
    };
}

function normalizeTriageFileList(raw) {
    const items = raw?.$values ?? raw;
    if (!Array.isArray(items)) return [];
    return items.map(normalizeTriageFile);
}

function normalizeTriageBatch(dto) {
    if (!dto || typeof dto !== 'object') return dto;
    return {
        batchReferenceId: dto.batchReferenceId ?? dto.BatchReferenceId,
        name: dto.name ?? dto.Name ?? null,
        notes: dto.notes ?? dto.Notes ?? null,
        createdAtUtc: dto.createdAtUtc ?? dto.CreatedAtUtc,
        ticketCount: dto.ticketCount ?? dto.TicketCount ?? 0,
        ticketReferenceIds: dto.ticketReferenceIds ?? dto.TicketReferenceIds ?? [],
        relevantFiles: normalizeTriageFileList(dto.relevantFiles ?? dto.RelevantFiles),
    };
}

function normalizeTicketGroup(dto) {
    if (!dto || typeof dto !== 'object') return dto;
    return {
        referenceId: dto.referenceId ?? dto.ReferenceId,
        name: dto.name ?? dto.Name,
        color: dto.color ?? dto.Color,
        createdAtUtc: dto.createdAtUtc ?? dto.CreatedAtUtc,
        updatedAtUtc: dto.updatedAtUtc ?? dto.UpdatedAtUtc,
        ticketCount: dto.ticketCount ?? dto.TicketCount ?? 0,
    };
}

function normalizeTicketGroupList(raw) {
    const items = raw?.$values ?? raw;
    if (!Array.isArray(items)) return [];
    return items.map(normalizeTicketGroup);
}

function normalizeAttachment(dto) {
    if (!dto || typeof dto !== 'object') return dto;
    return {
        ...dto,
        referenceId: dto.referenceId ?? dto.ReferenceId,
        fileName: dto.fileName ?? dto.FileName,
        contentType: dto.contentType ?? dto.ContentType,
        fileSize: dto.fileSize ?? dto.FileSize,
        fileSizeBytes: dto.fileSizeBytes ?? dto.FileSizeBytes,
        storageKey: dto.storageKey ?? dto.StorageKey,
        uploadedAt: dto.uploadedAt ?? dto.UploadedAt,
    };
}

function normalizeAttachmentList(raw) {
    const items = raw?.$values ?? raw;
    if (!Array.isArray(items)) return [];
    return items.map(normalizeAttachment);
}

function normalizePresignedAttachment(dto) {
    if (!dto || typeof dto !== 'object') return dto;
    return {
        ...dto,
        uploadUrl: dto.uploadUrl ?? dto.UploadUrl,
        storageKey: dto.storageKey ?? dto.StorageKey,
    };
}

const ticketApi = {
    fetchTicketReporter: async (referenceId) => {
        const res = await fetch(`${SUPPORT_URL()}/${encodeURIComponent(referenceId)}/reporter`, { headers: getAuthHeaders() });
        return handleResponse(res);
    },
    createTicket: async ({ type, severity, title, description, phiStatus }) => {
        const organizationReferenceId = getUserInfoFromToken()?.organizationReferenceId;
        const res = await fetch(SUPPORT_URL(), {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
                type,
                severity,
                title,
                description,
                phiStatus,
                ...(organizationReferenceId ? { organizationReferenceId } : {})
            })
        });
        const result = await handleResponse(res);
        return normalizeTicket(result);
    },

    fetchTickets: async () => {
        const res = await fetch(SUPPORT_URL(), {
            headers: getAuthHeaders()
        });
        const result = await handleResponse(res);
        return normalizeTicketList(result);
    },

    fetchTicketById: async (referenceId) => {
        const res = await fetch(`${SUPPORT_URL()}/${referenceId}`, {
            headers: getAuthHeaders()
        });
        const result = await handleResponse(res);
        return normalizeTicket(result);
    },

    updateTicket: async (referenceId, { type, severity, title, description, phiStatus }) => {
        const res = await fetch(`${SUPPORT_URL()}/${referenceId}`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({ type, severity, title, description, phiStatus })
        });
        const result = await handleResponse(res);
        return normalizeTicket(result);
    },

    deleteTicket: async (referenceId) => {
        const res = await fetch(`${SUPPORT_URL()}/${referenceId}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        return handleNoContentResponse(res);
    },

    updateTicketStatus: async (referenceId, status) => {
        const res = await fetch(`${SUPPORT_URL()}/${referenceId}/status`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({ status })
        });
        return handleResponse(res);
    },

    updateTicketFlag: async (referenceId, isFlagged) => {
        const res = await fetch(`${SUPPORT_URL()}/${referenceId}/flag`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({ isFlagged })
        });
        const result = await handleResponse(res);
        return normalizeTicket(result);
    },

    updateTicketPhiStatus: async (referenceId, { phiStatus, exclusionReason }) => {
        const res = await fetch(`${SUPPORT_URL()}/${referenceId}/phi-status`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({ phiStatus, exclusionReason })
        });
        const result = await handleResponse(res);
        return normalizeTicket(result);
    },

    updateTicketAiProcessing: async (referenceId, { excludeFromAiProcessing, exclusionReason }) => {
        const res = await fetch(`${SUPPORT_URL()}/${referenceId}/ai-processing`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({ excludeFromAiProcessing, exclusionReason })
        });
        const result = await handleResponse(res);
        return normalizeTicket(result);
    },

    dispatchTicketToSlack: async (referenceId) => {
        const res = await fetch(`${SUPPORT_URL()}/${encodeURIComponent(referenceId)}/slack-investigation`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    getAttachmentPresignedUrl: async (referenceId, { fileName, contentType, fileSize }) => {
        const res = await fetch(`${SUPPORT_URL()}/${referenceId}/attachments/presign`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ fileName, contentType, fileSize, fileSizeBytes: fileSize })
        });
        const result = await handleResponse(res);
        return normalizePresignedAttachment(result);
    },

    registerAttachment: async (referenceId, { fileName, contentType, fileSize, storageKey }) => {
        const res = await fetch(`${SUPPORT_URL()}/${referenceId}/attachments`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ fileName, contentType, fileSize, fileSizeBytes: fileSize, storageKey })
        });
        const result = await handleResponse(res);
        return normalizeAttachment(result);
    },

    getAttachmentDownloadUrl: async (referenceId, attachmentReferenceId) => {
        const res = await fetch(
            `${SUPPORT_URL()}/${referenceId}/attachments/${attachmentReferenceId}`,
            { headers: getAuthHeaders() }
        );
        return handleResponse(res);
    },

    uploadFileToS3: async (uploadUrl, file) => {
        const res = await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': file.type || 'application/octet-stream' },
            body: file
        });
        if (!res.ok) {
            throw new Error(`S3 upload failed: ${res.status} ${res.statusText}`);
        }
        return { success: true };
    },

    /**
     * Full attachment upload flow for a single file:
     * 1. Get presigned URL  2. Upload to S3  3. Register with backend
     */
    uploadAttachment: async (ticketReferenceId, file) => {
        const contentType = file.type || 'application/octet-stream';
        const { uploadUrl, storageKey } = await ticketApi.getAttachmentPresignedUrl(
            ticketReferenceId,
            { fileName: file.name, contentType, fileSize: file.size }
        );
        await ticketApi.uploadFileToS3(uploadUrl, file);
        return ticketApi.registerAttachment(ticketReferenceId, {
            fileName: file.name,
            contentType,
            fileSize: file.size,
            storageKey
        });
    },

    fetchTicketComments: async (referenceId) => {
        const res = await fetch(`${SUPPORT_URL()}/${referenceId}/comments`, {
            headers: getAuthHeaders()
        });
        const result = await handleResponse(res);
        return normalizeCommentList(result);
    },

    postTicketComment: async (referenceId, { body }) => {
        const res = await fetch(`${SUPPORT_URL()}/${referenceId}/comments`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ body })
        });
        const result = await handleResponse(res);
        return normalizeComment(result);
    },

    searchTicketTriageFileCatalog: async ({ repo, query = '', limit = 50 }) => {
        const params = new URLSearchParams({
            repo,
            limit: String(limit),
        });
        if (query) params.set('query', query);

        const res = await fetch(
            `${SUPPORT_BASE_URL()}/ticket-triage-file-catalog?${params.toString()}`,
            { headers: getAuthHeaders() }
        );
        const result = await handleResponse(res);
        return normalizeTriageFileList(result);
    },

    syncTicketTriageFileCatalog: async ({ repo, files }) => {
        const res = await fetch(`${SUPPORT_BASE_URL()}/ticket-triage-file-catalog/sync`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ repo, files })
        });
        const result = await handleResponse(res);
        return normalizeTriageFileList(result);
    },

    createTicketTriageBatch: async ({ name, notes, ticketReferenceIds, relevantFiles }) => {
        const res = await fetch(`${SUPPORT_BASE_URL()}/ticket-triage-batches`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ name, notes, ticketReferenceIds, relevantFiles })
        });
        const result = await handleResponse(res);
        return normalizeTriageBatch(result);
    },

    fetchTicketGroups: async () => {
        const res = await fetch(`${SUPPORT_BASE_URL()}/ticket-groups`, {
            headers: getAuthHeaders()
        });
        const result = await handleResponse(res);
        return normalizeTicketGroupList(result);
    },

    createTicketGroup: async ({ name, color, ticketReferenceIds = [] }) => {
        const res = await fetch(`${SUPPORT_BASE_URL()}/ticket-groups`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ name, color, ticketReferenceIds })
        });
        const result = await handleResponse(res);
        return normalizeTicketGroup(result);
    },

    updateTicketGroup: async (groupReferenceId, { name, color }) => {
        const res = await fetch(`${SUPPORT_BASE_URL()}/ticket-groups/${groupReferenceId}`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({ name, color })
        });
        const result = await handleResponse(res);
        return normalizeTicketGroup(result);
    },

    deleteTicketGroup: async (groupReferenceId) => {
        const res = await fetch(`${SUPPORT_BASE_URL()}/ticket-groups/${groupReferenceId}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        return handleNoContentResponse(res);
    },

    assignTicketGroup: async ({ ticketReferenceIds, groupReferenceId }) => {
        const res = await fetch(`${SUPPORT_BASE_URL()}/ticket-groups/assignments`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({ ticketReferenceIds, groupReferenceId })
        });
        return handleNoContentResponse(res);
    }
};

export default ticketApi;
